import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { inrFull, pct } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const F = ({ label, children }) => (
  <label className="block"><span className="text-sm font-semibold text-slate-600">{label}</span><div className="mt-1.5">{children}</div></label>
);

export function StaffPicker({ value, onChange, storeId, testid = "gms-staff-select", allLabel }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/users", { params: { role: "staff", store_id: storeId && storeId !== "all" ? storeId : undefined } })
      .then((r) => setRows(r.data.filter((u) => u.active !== false)));
  }, [storeId]);
  return (
    <Select value={value || (allLabel ? "all" : undefined)} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder="Select staff" /></SelectTrigger>
      <SelectContent>
        {allLabel && <SelectItem value="all">{allLabel}</SelectItem>}
        {rows.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.employee_id}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function useForm(initial) {
  const [f, setF] = useState(initial);
  return [f, (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e })), setF];
}

function Shell({ title, open, onClose, onSubmit, busy, children, submitLabel, testid, disabled }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">{children}</div>
        <DialogFooter>
          <Button disabled={busy || disabled} onClick={onSubmit} className="h-12 w-full bg-[#011E38] hover:bg-[#032C52]" data-testid={`${testid}-submit`}>{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useSubmit(path, onDone, msg) {
  const [busy, setBusy] = useState(false);
  const run = async (body) => {
    setBusy(true);
    try { await api.post(path, body); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, run];
}

const n = (v) => Number(v) || 0;

export function PlanDialog({ open, onClose, onDone }) {
  const { user, settings, storeId } = useAuth();
  const [f, set] = useForm({ customer_name: "", mobile: "", plan_ref: "", monthly_amount: "", start_date: settings.effective_date,
    instalments: settings.gms_default_instalments || 10, staff_id: "", first_paid: true });
  const [busy, run] = useSubmit("/gms/plans", onDone, "GMS plan saved — payment schedule created");
  const ok = f.customer_name.trim() && f.mobile.trim() && f.plan_ref.trim() && n(f.monthly_amount) > 0 && n(f.instalments) > 0 && (user.role === "staff" || f.staff_id);
  return (
    <Shell open={open} onClose={onClose} title="New GMS Plan" testid="gms-plan-dialog" busy={busy} disabled={!ok} submitLabel="Save & create schedule"
      onSubmit={() => run({ ...f, monthly_amount: n(f.monthly_amount), instalments: Math.round(n(f.instalments)), staff_id: f.staff_id || undefined })}>
      {user.role !== "staff" && <F label="Staff"><StaffPicker value={f.staff_id} onChange={set("staff_id")} storeId={storeId} /></F>}
      <F label="Customer Name"><Input value={f.customer_name} onChange={set("customer_name")} className="h-11" data-testid="gms-plan-customer-input" /></F>
      <F label="Customer Mobile Number"><Input inputMode="tel" value={f.mobile} onChange={set("mobile")} className="h-11" data-testid="gms-plan-mobile-input" /></F>
      <F label="GMS Plan ID / Reference"><Input value={f.plan_ref} onChange={set("plan_ref")} className="h-11 uppercase" data-testid="gms-plan-ref-input" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Monthly GMS Amount (₹)"><Input type="number" min="0" value={f.monthly_amount} onChange={set("monthly_amount")} className="h-11 num" data-testid="gms-plan-amount-input" /></F>
        <F label="Number of Instalments"><Input type="number" min="1" value={f.instalments} onChange={set("instalments")} className="h-11 num" data-testid="gms-plan-instalments-input" /></F>
      </div>
      <F label="Plan Start Date"><Input type="date" max={settings.effective_date} value={f.start_date} onChange={set("start_date")} className="h-11" data-testid="gms-plan-start-input" /></F>
      <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={f.first_paid} onCheckedChange={(v) => set("first_paid")(!!v)} data-testid="gms-plan-first-paid" /> First instalment paid on start date</label>
      {n(f.monthly_amount) > 0 && <div className="rounded-xl bg-[#F7F3EA] px-4 py-3 text-sm text-[#011E38]" data-testid="gms-plan-preview">{Math.round(n(f.instalments))} monthly payments of <b>{inrFull(f.monthly_amount)}</b> · total {inrFull(n(f.monthly_amount) * Math.round(n(f.instalments)))}</div>}
    </Shell>
  );
}

const Calc = ({ rows, testid }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={testid}>
    {rows.map(([l, v]) => <div key={l} className="rounded-xl bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">{l}</div><div className="num font-bold text-lg text-[#011E38]">{v}</div></div>)}
  </div>
);

function useCustomers() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/gms/plans").then((r) => setPlans(r.data)); }, []);
  return plans;
}

export function ReenrollDialog({ open, onClose, onDone }) {
  const { user, settings, storeId } = useAuth();
  const plans = useCustomers();
  const [f, set, setF] = useForm({ customer_name: "", mobile: "", plan_ref: "", prev_monthly: "", new_monthly: "", status: "reenrolled", date: settings.effective_date, remarks: "", staff_id: "" });
  const [busy, run] = useSubmit("/gms/reenrollments", onDone, "Re-enrollment recorded");
  const pickCustomer = (e) => {
    const v = e.target.value, p = plans.find((x) => x.customer_name === v);
    setF((x) => ({ ...x, customer_name: v, ...(p ? { mobile: p.mobile, plan_ref: p.plan_ref, prev_monthly: p.monthly_amount } : {}) }));
  };
  const yes = f.status === "reenrolled";
  const prev = n(f.prev_monthly), nw = yes ? n(f.new_monthly) : 0;
  const ok = f.customer_name.trim() && prev > 0 && (!yes || nw > 0) && (user.role === "staff" || f.staff_id);
  return (
    <Shell open={open} onClose={onClose} title="GMS Re-enrollment" testid="gms-reenroll-dialog" busy={busy} disabled={!ok} submitLabel="Save re-enrollment"
      onSubmit={() => run({ ...f, prev_monthly: prev, new_monthly: nw, staff_id: f.staff_id || undefined })}>
      <p className="text-xs text-slate-500 -mt-2">Record every matured GMS customer here, whether or not they re-enrolled.</p>
      {user.role !== "staff" && <F label="Staff"><StaffPicker value={f.staff_id} onChange={set("staff_id")} storeId={storeId} /></F>}
      <F label="Customer Name / Existing GMS Customer">
        <Input list="gms-customers" value={f.customer_name} onChange={pickCustomer} className="h-11" data-testid="gms-reenroll-customer-input" />
        <datalist id="gms-customers">{[...new Set(plans.map((p) => p.customer_name))].map((c) => <option key={c} value={c} />)}</datalist>
      </F>
      <F label="Re-enrollment Status">
        <div className="grid grid-cols-2 gap-2" data-testid="gms-reenroll-status">
          {[["reenrolled", "RE-ENROLLED"], ["not_reenrolled", "NOT RE-ENROLLED"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setF((x) => ({ ...x, status: v, new_monthly: v === "reenrolled" ? x.new_monthly : "0" }))}
              className={`h-12 rounded-xl border-2 text-sm font-bold transition-colors ${f.status === v ? (v === "reenrolled" ? "border-green-700 bg-green-700 text-white" : "border-red-700 bg-red-700 text-white") : "border-slate-200 text-slate-600"}`}
              data-testid={`gms-reenroll-status-${v}`}>{l}</button>
          ))}
        </div>
      </F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Previous Monthly GMS (₹)"><Input type="number" min="0" value={f.prev_monthly} onChange={set("prev_monthly")} className="h-11 num" data-testid="gms-reenroll-prev-input" /></F>
        <F label="New Monthly GMS (₹)"><Input type="number" min="0" value={yes ? f.new_monthly : "0"} disabled={!yes} onChange={set("new_monthly")} className="h-11 num" data-testid="gms-reenroll-new-input" /></F>
      </div>
      <F label="Re-enrollment Date"><Input type="date" max={settings.effective_date} value={f.date} onChange={set("date")} className="h-11" data-testid="gms-reenroll-date-input" /></F>
      <F label="Remarks"><Input value={f.remarks} onChange={set("remarks")} className="h-11" data-testid="gms-reenroll-remarks-input" /></F>
      {prev > 0 && yes && nw > 0 && <Calc testid="gms-reenroll-calc" rows={[["New Plan", inrFull(nw)], ["Re-enrollment", pct(nw / prev * 100, 0)], ["Increase", inrFull(nw - prev)], ["Increase %", pct((nw - prev) / prev * 100, 0)]]} />}
      {prev > 0 && !yes && <Calc testid="gms-reenroll-calc" rows={[["Previous Monthly", inrFull(prev)], ["New Monthly", inrFull(0)], ["Status", "NOT RE-ENROLLED"], ["Re-enrollment %", "0%"]]} />}
    </Shell>
  );
}

export function UpsellDialog({ open, onClose, onDone }) {
  const { user, settings, storeId } = useAuth();
  const plans = useCustomers();
  const [f, set, setF] = useForm({ customer_name: "", plan_ref: "", matured_value: "", sale_value: "", date: settings.effective_date, remarks: "", staff_id: "" });
  const [busy, run] = useSubmit("/gms/upsells", onDone, "Upselling recorded");
  const pickRef = (e) => {
    const v = e.target.value.toUpperCase(), p = plans.find((x) => x.plan_ref === v);
    setF((x) => ({ ...x, plan_ref: v, ...(p ? { customer_name: p.customer_name } : {}) }));
  };
  const mat = n(f.matured_value), sale = n(f.sale_value), up = Math.max(sale - mat, 0);
  const ok = f.customer_name.trim() && mat > 0 && f.sale_value !== "" && (user.role === "staff" || f.staff_id);
  return (
    <Shell open={open} onClose={onClose} title="GMS Upselling" testid="gms-upsell-dialog" busy={busy} disabled={!ok} submitLabel="Save upselling"
      onSubmit={() => run({ ...f, matured_value: mat, sale_value: sale, staff_id: f.staff_id || undefined })}>
      {user.role !== "staff" && <F label="Staff"><StaffPicker value={f.staff_id} onChange={set("staff_id")} storeId={storeId} /></F>}
      <F label="GMS Plan / Reference">
        <Input list="gms-refs" value={f.plan_ref} onChange={pickRef} className="h-11 uppercase" data-testid="gms-upsell-ref-input" />
        <datalist id="gms-refs">{plans.map((p) => <option key={p.id} value={p.plan_ref}>{p.customer_name}</option>)}</datalist>
      </F>
      <F label="Customer Name"><Input value={f.customer_name} onChange={set("customer_name")} className="h-11" data-testid="gms-upsell-customer-input" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Matured GMS Value (₹)"><Input type="number" min="0" value={f.matured_value} onChange={set("matured_value")} className="h-11 num" data-testid="gms-upsell-matured-input" /></F>
        <F label="Jewellery Sale Value (₹)"><Input type="number" min="0" value={f.sale_value} onChange={set("sale_value")} className="h-11 num" data-testid="gms-upsell-sale-input" /></F>
      </div>
      <F label="Date"><Input type="date" max={settings.effective_date} value={f.date} onChange={set("date")} className="h-11" data-testid="gms-upsell-date-input" /></F>
      <F label="Remarks"><Input value={f.remarks} onChange={set("remarks")} className="h-11" data-testid="gms-upsell-remarks-input" /></F>
      {mat > 0 && f.sale_value !== "" && <Calc testid="gms-upsell-calc" rows={[["Matured GMS", inrFull(mat)], ["Jewellery Sale", inrFull(sale)], ["Upselling Value", inrFull(up)], ["Upselling %", pct(up / mat * 100, 0)]]} />}
    </Shell>
  );
}

export function PayDialog({ payment, onClose, onDone }) {
  const { settings } = useAuth();
  const [f, set] = useForm({ paid_date: settings.effective_date, amount: payment.amount, reference: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/gms/payments/${payment.id}/pay`, { ...f, amount: n(f.amount) }); toast.success("Payment marked paid"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell open onClose={onClose} title={`Mark paid · #${payment.number}`} testid="gms-pay-dialog" busy={busy} disabled={n(f.amount) <= 0} submitLabel="Mark as PAID" onSubmit={save}>
      <div className="text-sm text-slate-600">{payment.customer_name} · {payment.plan_ref}</div>
      <F label="Payment Date"><Input type="date" max={settings.effective_date} value={f.paid_date} onChange={set("paid_date")} className="h-11" data-testid="gms-pay-date-input" /></F>
      <F label="Amount (₹)"><Input type="number" min="0" value={f.amount} onChange={set("amount")} className="h-11 num" data-testid="gms-pay-amount-input" /></F>
      <F label="Payment Reference (optional)"><Input value={f.reference} onChange={set("reference")} className="h-11" data-testid="gms-pay-ref-input" /></F>
    </Shell>
  );
}
